import { motion } from "framer-motion";
import enWhoweareData from "~/data/about-us/whoweare.json";
import gujWhoweareData from "~/data/about-us/whoweare_gu.json";
import { useFontStyle } from "~/hook/useFontStyle";
import useIntersectionObserver from "~/hook/useIntersectionObserver";
import { useLanguageParams } from "~/hook/useLanguageParams";
import { cn } from "~/lib/utils";
import { BACKGROUND_IMAGE_STYLE } from "~/routes/$lang._index";

// Hero Section
const HeroSection = ({ whoweareData }: { whoweareData: typeof enWhoweareData }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { medium } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="relative w-full mb-5"
    >
      <div className="w-full flex justify-center items-center px-4 md:px-6">
        <div className="w-full max-w-6xl text-center">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={isVisible ? { scale: 1, opacity: 1 } : { scale: 0.9, opacity: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <h1 className={cn("text-3xl md:text-4xl font-bold text-textColor mb-4", medium)}>{whoweareData.title}</h1>
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-primaryColor to-transparent mx-auto mb-6" />
            <p className={cn("text-lg md:text-xl text-textSecondaryColor italic max-w-2xl mx-auto", medium)}>{whoweareData.subTitle}</p>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

// Introduction Section
const IntroductionSection = ({ whoweareData }: { whoweareData: typeof enWhoweareData }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, x: -40 }}
      animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: -40 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className="mb-12 md:mb-20 px-4 md:px-6"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div className="relative">
          {/* Decorative icon */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={isVisible ? { scale: 1, rotate: 0 } : { scale: 0, rotate: -180 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="absolute -top-6 left-6 md:left-12 w-12 h-12 bg-primaryColor rounded-full flex items-center justify-center shadow-lg z-10"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
          </motion.div>

          <div className="p-8 md:p-12 rounded-2xl " style={BACKGROUND_IMAGE_STYLE}>
            {whoweareData.content.introduction.description.length > 0 &&
              whoweareData.content.introduction.description.map((desc, index) => (
                <p
                  key={index}
                  className={cn("text-textSecondaryColor text-base paragraph-text-size 2xl:text-lg leading-relaxed text-justify", normalInter)}
                >
                  {desc}
                </p>
              ))}
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

// Timeline Block Component for alternating layout
const TimelineSection = ({ section, index }: { section: (typeof enWhoweareData.content.sections)[0]; index: number }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();
  const isEven = index % 2 === 0;

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: "easeOut" },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.9, x: isEven ? -40 : 40 },
    visible: {
      opacity: 1,
      scale: 1,
      x: 0,
      transition: { duration: 0.7, ease: "easeOut", delay: 0.1 },
    },
  };

  const contentVariants = {
    hidden: { opacity: 0, x: isEven ? 40 : -40 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.7, ease: "easeOut", delay: 0.2 },
    },
  };

  return (
    <motion.div ref={ref} initial="hidden" animate={isVisible ? "visible" : "hidden"} variants={containerVariants} className="mb-16 md:mb-24">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
        {/* Timeline number and connecting line */}
        <div className="hidden md:flex md:col-span-2 items-center justify-between mb-4">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={isVisible ? { scale: 1, rotate: 0 } : { scale: 0, rotate: -180 }}
            transition={{ duration: 0.6, type: "spring", stiffness: 100 }}
            className="w-12 h-12 rounded-full bg-gradient-to-br from-primaryColor to-primaryColor/80 flex items-center justify-center text-white font-bold text-lg shadow-lg border-4 border-white"
          >
            {index + 1}
          </motion.div>
          <motion.div
            initial={{ scaleX: 0 }}
            animate={isVisible ? { scaleX: 1 } : { scaleX: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="flex-grow h-1 bg-gradient-to-r from-primaryColor/30 to-primaryColor/10 origin-left"
          />
        </div>

        {/* Image and Content - using flex direction to control order */}
        <div className={cn("flex flex-col md:flex-row gap-8 md:gap-12 md:col-span-2 items-center", isEven ? "md:flex-row-reverse" : "")}>
          {/* Image Section */}
          <motion.div
            variants={imageVariants}
            className="relative overflow-hidden rounded-2xl shadow-xl border-2 border-primaryColor/20 flex-shrink-0 w-full md:w-1/2"
          >
            <div className="relative aspect-auto overflow-hidden rounded-xl">
              <motion.img
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.5 }}
                src={section.imageSrc}
                alt={section.imageAlt}
                className="w-full h-full object-contain z-20"
              />
              <motion.div
                initial={{ opacity: 0 }}
                whileHover={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="absolute inset-0 bg-gradient-to-t from-primaryColor/20 to-transparent"
              />
            </div>
          </motion.div>

          {/* Content Section */}
          <motion.div variants={contentVariants} className="w-full md:w-1/2 flex flex-col justify-center">
            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className={cn("text-3xl md:text-3xl font-bold text-textColor mb-4", medium)}
            >
              {section.title}
            </motion.h2>

            {/* Decorative line */}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={isVisible ? { scaleX: 1 } : { scaleX: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="origin-left w-16 h-1.5 bg-gradient-to-r from-primaryColor to-primaryColor/50 rounded-full mb-6"
            />

            {section.content.map((paragraph, idx) => (
              <motion.p
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className={cn("text-base paragraph-text-size 2xl:text-lg text-textSecondaryColor leading-relaxed mb-6", normalInter)}
              >
                {paragraph}
              </motion.p>
            ))}

            {section.list && (
              <motion.ul
                initial={{ opacity: 0, y: 10 }}
                animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="list-disc list-inside text-textSecondaryColor mb-6"
              >
                {section.list.map((item, idx) => (
                  <li key={idx} className={cn("text-base paragraph-text-size 2xl:text-lg leading-relaxed", normalInter)}>
                    {item}
                  </li>
                ))}
              </motion.ul>
            )}
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

// Main Component
function OurActivities() {
  const lang = useLanguageParams();
  const whoweareData = lang === "gu" ? gujWhoweareData : enWhoweareData;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  return (
    <motion.div className="w-full">
      {/* Hero Section */}
      <HeroSection whoweareData={whoweareData} />
      {/* Introduction Section */}
      <IntroductionSection whoweareData={whoweareData} />

      {/* Timeline Activities Section */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
        className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 mb-16 md:mb-28"
      >
        <motion.div className="space-y-12 md:space-y-20">
          {whoweareData.content.sections.map((section, index) => (
            <TimelineSection key={index} section={section} index={index} />
          ))}
        </motion.div>
      </motion.section>
    </motion.div>
  );
}

export default OurActivities;
